import { useState } from "react";
import { Play } from "lucide-react";
import { Link } from "@/lib/router";
import type { Article } from "@/data/articles";
import { sectionById } from "@/data/sections";
import { timeAgo } from "@/lib/format";

export const articleHref = (a: Article) => `/${a.section}/${a.slug}`;

/** Image with a branded fallback so a broken photo never leaves a hole in the page. */
export function Img({ src, alt, className = "", video }: { src: string; alt: string; className?: string; video?: boolean }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className={`relative overflow-hidden bg-wash ${className}`}>
      {failed ? (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-ink to-brand-dark">
          <span className="font-[family-name:var(--font-masthead)] text-3xl font-black text-white/80">BB</span>
        </div>
      ) : (
        <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} className="absolute inset-0 size-full object-cover" />
      )}
      {video && (
        <span className="absolute bottom-2 left-2 flex size-10 items-center justify-center rounded-full bg-brand text-white shadow">
          <Play className="size-5 fill-white" />
        </span>
      )}
    </div>
  );
}

export function Kicker({ a }: { a: Article }) {
  return <span className="kicker">{a.kicker ?? sectionById(a.section)?.label}</span>;
}

export function Meta({ a }: { a: Article }) {
  return (
    <p className="mt-1.5 text-[12px] font-semibold uppercase tracking-wide text-muted">
      {timeAgo(a.hoursAgo)}
      {a.city ? ` · ${a.city}` : ""}
    </p>
  );
}

/** The giant front-page splash: huge photo, all-caps headline. */
export function LeadStory({ a }: { a: Article }) {
  return (
    <Link href={articleHref(a)} className="story block">
      <Img src={a.image} alt={a.imageCaption ?? a.title} className="aspect-[16/10]" video={a.video} />
      <div className="pt-3">
        {a.exclusive && (
          <span className="mr-2 inline-block bg-brand px-1.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wider text-white">
            Exclusive
          </span>
        )}
        <Kicker a={a} />
        <h1 className="splash mt-1 text-[3.2rem] text-ink sm:text-[4.6rem] lg:text-[5.4rem]">{a.splash ?? a.title}</h1>
        <p className="headline mt-2 text-xl text-ink sm:text-2xl">{a.title}</p>
        <p className="mt-2 font-[family-name:var(--font-body)] text-[17px] text-ink-soft">{a.dek}</p>
        <Meta a={a} />
      </div>
    </Link>
  );
}

/** Image on top, headline below. */
export function CardStory({ a, size = "md" }: { a: Article; size?: "sm" | "md" | "lg" }) {
  const h = size === "lg" ? "text-[1.7rem]" : size === "md" ? "text-[1.2rem]" : "text-[1.02rem]";
  return (
    <Link href={articleHref(a)} className="story block">
      <Img src={a.image} alt={a.title} className="aspect-[3/2]" video={a.video} />
      <div className="pt-2">
        <Kicker a={a} />
        <h3 className={`headline mt-0.5 ${h}`}>{a.title}</h3>
        {size !== "sm" && <p className="mt-1 font-[family-name:var(--font-body)] text-[15px] leading-snug text-ink-soft">{a.dek}</p>}
        <Meta a={a} />
      </div>
    </Link>
  );
}

/** Headline beside a small thumbnail: the dense list look of a tabloid front. */
export function ListStory({ a, thumb = true }: { a: Article; thumb?: boolean }) {
  return (
    <Link href={articleHref(a)} className="story flex gap-3 border-b border-rule py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <Kicker a={a} />
        <h3 className="headline mt-0.5 text-[1.05rem]">{a.title}</h3>
        <Meta a={a} />
      </div>
      {thumb && <Img src={a.image} alt="" className="aspect-square w-24 shrink-0 sm:w-28" video={a.video} />}
    </Link>
  );
}

export function SectionHeader({ title, href }: { title: string; href?: string }) {
  const inner = (
    <h2 className="splash text-[2rem] text-ink group-hover:text-brand">
      {title}
      {href && <span className="ml-2 text-brand">›</span>}
    </h2>
  );
  return (
    <div className="mb-4 border-t-[6px] border-brand pt-2">
      {href ? (
        <Link href={href} className="group inline-block">
          {inner}
        </Link>
      ) : (
        inner
      )}
    </div>
  );
}
